import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  fetchSignInOptions, forgetMicrosoftSignIn, forgetRememberedUsername, getRememberedUsername, useAuth,
} from '@/contexts/AuthContext';
import { browserNavigation, resolveSignInReturn } from '@/components/entry/session';
import { describePasskeyError, isPasskeySupported, passkeyLabel } from '@/components/entry/passkeys';
import { useAppSettings } from '@/contexts/AppSettingsContext';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getErrorMessage } from '@/lib/errors';
import { formatTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  ArrowBigUp, CircleAlert, Clock, DraftingCompass, Eye, EyeOff, Fingerprint, FolderKanban, KeyRound,
  Loader2, LockKeyhole, LogIn, Package, ScanFace, ShieldCheck, WifiOff,
} from 'lucide-react';

const MAX_TRIES = 5;
const LOCK_MINUTES = 15;
const FORGOT_HELP_WIDTH = 200;
const FORGOT_HELP_OFFSET = 40;

const CAPABILITIES = [
  { icon: FolderKanban, title: 'Projects & 10 delivery stages', sub: 'Received Drawings → Handing over documents' },
  { icon: DraftingCompass, title: 'Shop drawings & BMS structures', sub: 'Buildings → floors → rooms → panels → devices' },
  { icon: Package, title: 'Stock across sites', sub: 'Abu Dhabi Store, site BOQs, LPOs and deliveries' },
];

/** 872 → "14:32" */
export const formatCountdown = (seconds) => {
  const total = Math.max(0, Math.ceil(Number(seconds) || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/** The server locks sign-in per device + username (trimmed, lower-cased), so the page does too. */
export const lockKey = (username) => String(username || '').trim().toLowerCase();

/** Unlock time as the rest of the app prints times (Asia/Dubai, 24 h): 1767… → "21:20". */
const clockTime = (time) => formatTime(time);

/** What went wrong with a sign-in attempt, in terms the page can explain. */
export function describeLoginError(error) {
  const response = error?.response;
  if (!response) return { kind: 'network' };
  const data = response.data || {};
  if (response.status === 401) {
    const left = Number(data.attempts_remaining);
    return { kind: 'invalid', attemptsRemaining: Number.isFinite(left) ? left : null };
  }
  if (response.status === 429) {
    const seconds = Number(data.retry_after) || Number(response.headers?.['retry-after']) || LOCK_MINUTES * 60;
    return { kind: 'locked', retryAfter: seconds };
  }
  if (response.status === 403) {
    // Right password, but an admin turned this account's sign-in off (Admin › Users) — the server says what to do.
    const message = getErrorMessage(error, '');
    const rest = message.replace(/^Sign-in is turned off for this account\.?\s*/i, '').trim();
    return { kind: 'blocked', message: rest || 'Ask an admin to turn it back on.' };
  }
  return { kind: 'other', message: getErrorMessage(error, 'Sign-in failed. Please try again.') };
}

/** ?code=&state= (Microsoft sent the browser back) or ?error=… (cancelled / refused at Microsoft). */
export function readMicrosoftCallback(search) {
  const params = new URLSearchParams(search || '');
  const code = params.get('code') || '';
  const state = params.get('state') || '';
  const error = params.get('error') || '';
  if (code && state) return { code, state };
  if (error && state) {
    const cancelled = error === 'access_denied' || /cancel/i.test(params.get('error_description') || '');
    return {
      error: cancelled
        ? { title: 'Microsoft 365 sign-in was cancelled.', message: 'Try again, or use your email and password.' }
        : { title: 'Microsoft 365 sign-in didn’t work.', message: 'Microsoft could not sign you in. Try again, or use your email and password.' },
    };
  }
  return null;
}

const isPhoneDevice = () => typeof navigator !== 'undefined' && /iPhone|iPod|Android.+Mobile/i.test(navigator.userAgent || '');

/** The four-square Microsoft mark (its own colours, as Microsoft asks for sign-in buttons). */
function MicrosoftMark({ muted }) {
  return (
    <svg viewBox="0 0 16 16" className={cn('h-4 w-4 shrink-0', muted && 'opacity-50 grayscale')} aria-hidden="true">
      <rect x="0" y="0" width="7.5" height="7.5" fill="#f25022" />
      <rect x="8.5" y="0" width="7.5" height="7.5" fill="#7fba00" />
      <rect x="0" y="8.5" width="7.5" height="7.5" fill="#00a4ef" />
      <rect x="8.5" y="8.5" width="7.5" height="7.5" fill="#ffb900" />
    </svg>
  );
}

const NOT_SET_UP_HINT = 'isn’t set up on this server yet — IT can switch it on in Admin › Integrations.';

function Callout({ tone = 'red', icon: Icon, children, testId }) {
  // role="alert" announces the message once when it appears. Anything inside that changes on its own
  // (the lock countdown) must be aria-hidden, or screen readers re-read the alert on every tick.
  return (
    <div
      role="alert"
      data-testid={testId}
      className={cn(
        'flex items-start gap-2.5 rounded-control border px-3.5 py-3 text-[13px] leading-[1.45]',
        tone === 'red' && 'border-[color:var(--danger-border)] bg-status-red-bg text-status-red-ink',
        tone === 'amber' && 'border-transparent bg-status-amber-bg text-status-amber-ink',
      )}
    >
      <Icon className="mt-px h-4 w-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/**
 * "Your session ended after 8 hours. Sign in to return to Luxor Tower › Stages." (01 #5) — shown when the
 * session ran out (or the server signed the user out) while they were working; after signing in the page
 * goes straight back there.
 */
export function SessionNotice({ reason, label, length }) {
  if (!reason) return null;
  const lead = reason === 'expired' ? `Your session ended after ${length || '8 hours'}.` : 'You were signed out.';
  return (
    <div
      role="status"
      data-testid="login-session-notice"
      className="mb-5 flex items-start gap-2.5 rounded-control border border-[color:var(--brand-soft-border)] bg-brand-soft px-3.5 py-3 text-[13px] leading-[1.45] text-ink-2"
    >
      <Clock className="mt-px h-4 w-4 shrink-0 text-brand-ink" aria-hidden="true" />
      <p className="min-w-0">
        {lead}{' '}
        {label ? <>Sign in to return to <b className="font-bold text-ink">{label}</b>.</> : 'Sign in to continue.'}
      </p>
    </div>
  );
}

const hasStoredToken = () => {
  try {
    return Boolean(localStorage.getItem('token'));
  } catch {
    return false;
  }
};

function BrandPanel({ appName }) {
  return (
    <aside className="relative hidden flex-col justify-between overflow-hidden bg-navy p-12 text-white lg:flex">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse at 15% 8%, rgb(128 200 4 / 0.22), transparent 45%)' }}
        aria-hidden="true"
      />
      <img
        src="/sara-eng-logo.svg"
        alt={appName}
        className="relative h-11 w-auto max-w-[200px] self-start object-contain"
        data-testid="login-brand-logo"
      />
      <div className="relative max-w-[400px]">
        <p className="text-[13px] font-semibold text-lime">Employee workspace</p>
        <h1 className="mt-2 font-heading text-[40px] font-black leading-[1.1] tracking-[-0.02em]">{appName}</h1>
        <p className="mt-3 text-[15px] leading-relaxed text-white/[0.78]">
          Projects, drawings, stock and approvals in one calm place for site and office teams.
        </p>
        <ul className="mt-7 space-y-4" data-testid="login-capabilities">
          {CAPABILITIES.map(({ icon: Icon, title, sub }) => (
            <li key={title} className="flex items-start gap-3">
              <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[10px] bg-white/[0.08] text-lime" aria-hidden="true">
                <Icon className="h-[17px] w-[17px]" strokeWidth={1.75} />
              </span>
              <span className="min-w-0">
                <span className="block text-[14px] font-semibold leading-snug">{title}</span>
                <span className="block text-[12.5px] leading-snug text-white/[0.66]">{sub}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
      <div className="relative flex items-center gap-2 text-[13px] text-white/[0.78]">
        <ShieldCheck className="h-4 w-4 text-lime" strokeWidth={1.75} aria-hidden="true" />
        <span>Signed access for company staff only</span>
      </div>
    </aside>
  );
}

const inputClasses = 'h-[52px] w-full rounded-control border border-line bg-surface px-3.5 text-base text-ink shadow-card transition-[border-color,box-shadow] placeholder:text-ink-4 focus:border-[color:var(--lime-600)] focus:shadow-focus focus:outline-none lg:h-11 lg:text-[14px]';

export default function LoginPage() {
  const rememberedUsername = useRef(getRememberedUsername()).current;
  const [username, setUsername] = useState(rememberedUsername);
  const [prefilled, setPrefilled] = useState(Boolean(rememberedUsername));
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // lockKey(username) → time the server unlocks it. Only the username that was locked is blocked here.
  const [locks, setLocks] = useState({});
  const [now, setNow] = useState(() => Date.now());
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);
  // The shell opens this page the moment the session runs out (the notice is already written); finish
  // signing out here, so the protected page behind is never asked to redirect a second time. Never for a
  // session this page has just started: that one is fresh, and ending it would silently loop back here.
  const signedInRef = useRef(false);
  // The reset help opens beside the card when there is room (wide desktop), otherwise under the link.
  const [forgotBeside, setForgotBeside] = useState(false);
  const placeForgotHelp = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setForgotBeside(window.innerWidth - rect.right >= FORGOT_HELP_OFFSET + FORGOT_HELP_WIDTH + 8);
  };
  const {
    login, sessionExpired, endSession, user, resumed, startMicrosoftSignIn, finishMicrosoftSignIn, signInWithPasskey,
  } = useAuth();
  const { appName } = useAppSettings();
  const navigate = useNavigate();
  const location = useLocation();
  // Why the user is here and where to go after signing in — read once (signing in clears the notice).
  const [returnInfo] = useState(() => resolveSignInReturn(location.search, { hasToken: hasStoredToken() }));
  useEffect(() => {
    if (sessionExpired && !signedInRef.current) endSession();
  }, [sessionExpired, endSession]);
  // Keep me signed in (01 #6) — opt-in, per sign-in.
  const [remember, setRemember] = useState(false);
  // Which extra ways to sign in this server offers (Microsoft 365 01 #8, passkeys 01 #9): off until it says so.
  const [options, setOptions] = useState(null);
  const [microsoftCallback] = useState(() => readMicrosoftCallback(location.search));
  const [busy, setBusy] = useState(microsoftCallback?.code ? 'microsoft' : '');
  const phone = useRef(isPhoneDevice()).current;
  const passkeyWord = useRef(passkeyLabel()).current;
  const passkeyBrowser = useRef(isPasskeySupported()).current;

  useEffect(() => {
    let cancelled = false;
    fetchSignInOptions().then((result) => { if (!cancelled) setOptions(result); });
    return () => { cancelled = true; };
  }, []);

  /** Signed in (any way): welcome, then the page the user came from (or the server's `next`), else home. */
  const goOn = useCallback((path) => {
    signedInRef.current = true;
    const target = path || returnInfo.path;
    if (target) toast.success(returnInfo.label && target === returnInfo.path ? `Welcome back — ${returnInfo.label}` : 'Welcome back');
    else toast.success(`Welcome to ${appName}`);
    navigate(target || '/', { replace: true });
  }, [appName, navigate, returnInfo.label, returnInfo.path]);

  // A remembered device (01 #6) signed itself back in while this page was open: go on to where the user was.
  useEffect(() => {
    if (resumed && user && !signedInRef.current) goOn('');
  }, [resumed, user, goOn]);

  // Microsoft 365 sent the browser back here (?code=&state=): finish signing in, or say why it did not work.
  const callbackHandled = useRef(false);
  useEffect(() => {
    if (!microsoftCallback || callbackHandled.current) return;
    callbackHandled.current = true;
    // The code is single use: take it out of the address bar (and history) at once.
    navigate('/login', { replace: true });
    if (microsoftCallback.error) {
      forgetMicrosoftSignIn();
      setError({ kind: 'sso', ...microsoftCallback.error });
      return;
    }
    (async () => {
      try {
        const result = await finishMicrosoftSignIn(microsoftCallback);
        goOn(result.next);
      } catch (err) {
        setError({ kind: 'sso', message: getErrorMessage(err, 'Try again, or use your email and password.') });
        setBusy('');
      }
    })();
  }, [finishMicrosoftSignIn, goOn, microsoftCallback, navigate]);

  const lockedUntil = locks[lockKey(username)] || null;
  const lockSeconds = lockedUntil ? Math.max(0, Math.ceil((lockedUntil - now) / 1000)) : 0;
  const locked = lockSeconds > 0;
  const anyLock = Object.keys(locks).length > 0;

  useEffect(() => {
    if (!anyLock) return undefined;
    const timer = setInterval(() => {
      const current = Date.now();
      setNow(current);
      setLocks((previous) => {
        const active = Object.entries(previous).filter(([, until]) => until > current);
        return active.length === Object.keys(previous).length ? previous : Object.fromEntries(active);
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [anyLock]);

  const handleUsernameChange = (event) => {
    setUsername(event.target.value);
    setPrefilled(false);
    // "2 tries left" was counted for the old username. (A lock is shown from `locks`, per username.)
    if (error) setError(null);
  };

  const notYou = () => {
    forgetRememberedUsername();
    setUsername('');
    setPassword('');
    setPrefilled(false);
    setError(null);
    requestAnimationFrame(() => usernameRef.current?.focus());
  };

  const trackCapsLock = useCallback((event) => {
    if (typeof event.getModifierState === 'function') setCapsLock(event.getModifierState('CapsLock'));
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (locked || loading || busy) return;
    setLoading(true);
    setError(null);
    try {
      await login(username.trim(), password, { remember: rememberOn && remember });
      goOn('');
    } catch (err) {
      const info = describeLoginError(err);
      if (info.kind === 'locked') {
        const current = Date.now();
        setNow(current);
        setLocks((previous) => ({ ...previous, [lockKey(username)]: current + info.retryAfter * 1000 }));
        setError(null);
      } else {
        setError(info);
      }
      if (info.kind === 'invalid') {
        // Keep the username; select the password so typing replaces it.
        requestAnimationFrame(() => {
          passwordRef.current?.focus();
          passwordRef.current?.select();
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const rememberOn = options?.remember_me?.enabled !== false;
  const rememberDays = options?.remember_me?.days || 14;
  const microsoftOn = Boolean(options?.microsoft_sso?.enabled);
  const passkeysOn = Boolean(options?.passkeys?.enabled);
  // "Not set up yet" only once the server has said so: while GET /auth/sign-in-options is on its way the
  // Microsoft button shows its neutral off label and the passkey line keeps its space empty.
  const optionsLoaded = options !== null;
  const microsoftOff = optionsLoaded && !microsoftOn;

  const continueWithMicrosoft = async () => {
    if (!microsoftOn || busy || loading) return;
    setBusy('microsoft');
    setError(null);
    try {
      const url = await startMicrosoftSignIn({ next: returnInfo.path, remember: rememberOn && remember });
      browserNavigation.assign(url);
    } catch (err) {
      setError({ kind: 'sso', message: getErrorMessage(err, 'It is not available right now. Use your email and password.') });
      setBusy('');
    }
  };

  const continueWithPasskey = async () => {
    if (!passkeysOn || busy || loading) return;
    setBusy('passkey');
    setError(null);
    try {
      await signInWithPasskey({ remember: rememberOn && remember });
      goOn('');
    } catch (err) {
      const browser = describePasskeyError(err);
      if (browser.kind !== 'cancelled') {
        setError({ kind: 'passkey', message: browser.message || getErrorMessage(err, 'Use your password instead.') });
      }
      setBusy('');
    }
  };

  const passkeyText = passkeyWord === 'a passkey' ? 'Sign in with a passkey' : `Sign in with ${passkeyWord}`;
  const PasskeyIcon = passkeyWord === 'Face ID' ? ScanFace : Fingerprint;

  const invalidHint = error?.kind === 'invalid' && error.attemptsRemaining !== null && error.attemptsRemaining <= 3
    ? `${error.attemptsRemaining} ${error.attemptsRemaining === 1 ? 'try' : 'tries'} left before sign-in locks for ${LOCK_MINUTES} minutes.`
    : `After ${MAX_TRIES} failed tries, sign-in locks for ${LOCK_MINUTES} minutes.`;

  return (
    <div
      className="app-theme-background flex min-h-[100dvh] flex-col lg:grid lg:grid-cols-[440px_1fr] xl:grid-cols-[560px_1fr]"
      data-testid="login-page"
    >
      <BrandPanel appName={appName} />

      {/* Mobile / tablet: navy band with the full logo (its wordmark is white), form sheet below. */}
      <div className="bg-navy px-5 pb-10 pt-[calc(env(safe-area-inset-top,0px)+28px)] text-white lg:hidden" data-testid="login-mobile-band">
        <img src="/sara-eng-logo.svg" alt={appName} className="h-[34px] w-auto object-contain" />
        <p className="mt-[18px] text-[12px] font-semibold text-lime">Employee workspace</p>
        <h1 className="mt-1 font-heading text-[26px] font-black leading-tight tracking-[-0.02em]">{appName}</h1>
      </div>

      <main className="relative -mt-5 flex flex-1 flex-col sm:items-center sm:px-6 sm:pb-10 lg:mt-0 lg:justify-center lg:px-8 lg:py-10">
        <div
          className="w-full flex-1 rounded-t-[20px] bg-surface px-5 pb-7 pt-[22px] sm:max-w-[480px] sm:flex-none sm:rounded-b-[20px] sm:shadow-raised lg:max-w-[440px] lg:rounded-card lg:border lg:border-line lg:p-8 lg:shadow-raised"
          data-testid="login-card"
        >
          <SessionNotice reason={returnInfo.reason} label={returnInfo.label} length={returnInfo.length} />
          <h2 className="font-heading text-[22px] font-black leading-tight tracking-[-0.01em] text-ink lg:text-[26px]">
            Sign in
          </h2>
          <p className="mt-1 text-[13.5px] text-ink-3">Use your company email and password.</p>

          {/* 01 #8 — Microsoft 365. Shown switched off (with why) until IT sets it up and an admin switches it on.
              Until the server says which ways it offers, the button stays in its neutral "off" look (no
              "Continue with…" that does nothing, no tag that appears a moment later). */}
          <div className="mt-6" data-testid="login-microsoft-block">
            <button
              type="button"
              onClick={continueWithMicrosoft}
              disabled={!microsoftOn || Boolean(busy) || loading}
              aria-describedby={microsoftOff ? 'login-microsoft-status' : undefined}
              title={microsoftOff ? `Microsoft 365 sign-in ${NOT_SET_UP_HINT}` : undefined}
              className={cn(
                'inline-flex h-[52px] w-full items-center justify-center gap-2.5 rounded-control border border-line bg-surface px-4 text-[15px] font-semibold shadow-card transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:h-11 lg:text-[14px]',
                microsoftOn ? 'text-ink hover:bg-surface-hover' : 'cursor-not-allowed text-ink-3 shadow-none',
              )}
              data-testid="login-microsoft"
              data-enabled={microsoftOn ? 'true' : 'false'}
            >
              {busy === 'microsoft' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <MicrosoftMark muted={!microsoftOn} />}
              <span className="whitespace-nowrap">
                {busy === 'microsoft' ? 'Signing in with Microsoft…' : microsoftOn ? 'Continue with Microsoft 365' : 'Microsoft 365 sign-in'}
              </span>
              {microsoftOff ? (
                <span id="login-microsoft-status" className="whitespace-nowrap rounded-full bg-[color:var(--status-gray-bg)] px-2 py-px text-[11px] font-semibold text-ink-3">
                  Not set up yet
                  <span className="sr-only"> — Microsoft 365 sign-in {NOT_SET_UP_HINT}</span>
                </span>
              ) : null}
            </button>
            <div className="mt-4 flex items-center gap-3 text-[12px] text-ink-3" aria-hidden="true">
              <span className="h-px flex-1 bg-[color:var(--line)]" />
              or with your email
              <span className="h-px flex-1 bg-[color:var(--line)]" />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="mt-4 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <div className="flex min-h-5 items-center justify-between gap-3">
                {/* A few accounts (e.g. "admin") have no @, so the field still takes a username. */}
                <Label htmlFor="username" className="text-[13px] font-semibold text-ink">Email or username</Label>
                {prefilled && username ? (
                  <button
                    type="button"
                    onClick={notYou}
                    className="-my-3 rounded py-3 text-[13px] font-semibold text-brand-ink underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:my-0 lg:py-0"
                    data-testid="login-not-you"
                  >
                    Not you?
                  </button>
                ) : null}
              </div>
              <input
                ref={usernameRef}
                id="username"
                name="username"
                type="text"
                value={username}
                onChange={handleUsernameChange}
                onKeyDown={trackCapsLock}
                onKeyUp={trackCapsLock}
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoFocus={!rememberedUsername}
                aria-describedby="username-help"
                className={inputClasses}
                data-testid="login-email"
                required
              />
              <p id="username-help" className="text-[12px] leading-snug text-ink-3">
                Usually your company email, e.g. name@saraeengg.com.
              </p>
            </div>

            {/* "Forgot password?" sits in the label row but comes AFTER the field in the DOM, so Tab goes
                email → password (→ show) → forgot, and a habitual Enter never opens the help instead. */}
            <div className="relative space-y-1.5">
              <div className="flex min-h-5 items-center">
                <Label htmlFor="password" className="text-[13px] font-semibold text-ink">Password</Label>
              </div>
              <div className="relative">
                <input
                  ref={passwordRef}
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={trackCapsLock}
                  onKeyUp={trackCapsLock}
                  onBlur={() => setCapsLock(false)}
                  autoComplete="current-password"
                  autoFocus={Boolean(rememberedUsername)}
                  className={cn(inputClasses, 'pr-12')}
                  data-testid="login-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  aria-controls="password"
                  className="absolute inset-y-0 right-1 my-auto grid h-11 w-11 place-items-center rounded-lg text-ink-3 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:h-9 lg:w-9"
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                </button>
              </div>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="absolute right-0 top-0 !mt-0 -translate-y-3 rounded py-3 text-[13px] font-semibold leading-5 text-brand-ink underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:translate-y-0 lg:py-0"
                    onPointerDown={placeForgotHelp}
                    onKeyDown={placeForgotHelp}
                    data-testid="login-forgot-password"
                  >
                    Forgot password?
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side={forgotBeside ? 'right' : 'bottom'}
                  align={forgotBeside ? 'center' : 'end'}
                  sideOffset={forgotBeside ? FORGOT_HELP_OFFSET : 6}
                  style={{ width: FORGOT_HELP_WIDTH }}
                  className="rounded-control border-line bg-surface p-3.5 text-[12.5px] leading-relaxed text-ink-2 shadow-raised" data-testid="login-forgot-password-help">
                  <p className="mb-1 flex items-center gap-1.5 text-[13px] font-semibold text-ink">
                    <KeyRound className="h-3.5 w-3.5" aria-hidden="true" />
                    Resetting a password
                  </p>
                  Passwords are reset by a <b className="text-ink">Super Admin</b> from <b className="text-ink">Admin › Users</b>. Ask your manager or IT.
                </PopoverContent>
              </Popover>
              {capsLock ? (
                <p className="flex items-center gap-1.5 text-[12.5px] font-semibold text-status-amber-ink" role="status" data-testid="login-capslock">
                  <ArrowBigUp className="h-3.5 w-3.5" aria-hidden="true" />
                  Caps Lock is on
                </p>
              ) : null}
            </div>

            {error?.kind === 'invalid' ? (
              <Callout icon={CircleAlert} testId="login-error">
                <b className="font-bold">Email or password is incorrect.</b> {invalidHint}
              </Callout>
            ) : null}
            {locked ? (
              <Callout icon={LockKeyhole} testId="login-error">
                <b className="font-bold">Too many attempts.</b> Sign-in for this account is locked —{' '}
                {/* The ticking time is visual only; screen readers get a fixed sentence, once. */}
                <span aria-hidden="true">
                  try again in <b className="font-bold tabular-nums" data-testid="login-lock-countdown">{formatCountdown(lockSeconds)}</b>
                </span>
                <span className="sr-only">try again at {clockTime(lockedUntil)}</span>.
                {' '}Forgot your password? A Super Admin can reset it.
              </Callout>
            ) : null}
            {error?.kind === 'network' ? (
              <Callout icon={WifiOff} testId="login-error">
                <b className="font-bold">Can’t reach the server.</b> Check your connection and try again.
              </Callout>
            ) : null}
            {error?.kind === 'blocked' ? (
              <Callout icon={LockKeyhole} testId="login-error">
                <b className="font-bold">Sign-in is turned off for this account.</b> {error.message}
              </Callout>
            ) : null}
            {error?.kind === 'other' ? (
              <Callout icon={CircleAlert} testId="login-error">{error.message}</Callout>
            ) : null}
            {error?.kind === 'sso' || error?.kind === 'passkey' ? (
              <Callout icon={CircleAlert} testId="login-error">
                <b className="font-bold">{error.title || (error.kind === 'sso' ? 'Microsoft 365 sign-in didn’t work.' : 'Passkey sign-in didn’t work.')}</b>{' '}
                {error.message}
              </Callout>
            ) : null}

            {rememberOn ? (
              <div className="flex min-h-[44px] items-center gap-2.5 lg:min-h-0" data-testid="login-remember-row">
                <Checkbox
                  id="remember"
                  checked={remember}
                  onCheckedChange={(value) => setRemember(value === true)}
                  className="h-[18px] w-[18px] rounded-[5px] border-[color:var(--navy-300)] data-[state=checked]:border-navy data-[state=checked]:bg-navy data-[state=checked]:text-white [&_svg]:h-3.5 [&_svg]:w-3.5"
                  aria-describedby="remember-help"
                  data-testid="login-remember"
                />
                <label htmlFor="remember" className="cursor-pointer select-none text-[14px] text-ink-2 lg:text-[13px]">
                  Keep me signed in on this {phone ? 'phone' : 'device'}
                  <span className="text-ink-3"> · {rememberDays} {rememberDays === 1 ? 'day' : 'days'}</span>
                </label>
                <span id="remember-help" className="sr-only">
                  Only on your own device: anyone using it stays signed in as you for {rememberDays} days, until you sign out.
                </span>
              </div>
            ) : null}

            <Button
              type="submit"
              className={cn(
                'h-[52px] w-full text-[15px] lg:h-[46px] lg:text-[14px]',
                locked && 'disabled:opacity-100 disabled:bg-[color:var(--navy-300)] disabled:text-white'
              )}
              disabled={loading || locked}
              aria-label={locked ? `Sign-in locked until ${clockTime(lockedUntil)}` : undefined}
              data-testid="login-submit"
            >
              {locked ? (
                <>
                  <LockKeyhole className="h-4 w-4" aria-hidden="true" />
                  Locked · <span className="tabular-nums">{formatCountdown(lockSeconds)}</span>
                </>
              ) : (
                <>
                  {loading ? 'Signing in…' : 'Sign in'}
                  <LogIn className="h-4 w-4" />
                </>
              )}
            </Button>

            {/* 01 #9 — passkey: Face ID / fingerprint, no username needed. Shown switched off until set up.
                Until the sign-in options arrive its space is kept but nothing is shown (`invisible`). */}
            {passkeyBrowser ? (
              phone ? (
                <button
                  type="button"
                  onClick={continueWithPasskey}
                  disabled={!passkeysOn || Boolean(busy) || loading}
                  title={passkeysOn || !optionsLoaded ? undefined : `Passkey sign-in ${NOT_SET_UP_HINT}`}
                  aria-hidden={optionsLoaded ? undefined : true}
                  className={cn(
                    'inline-flex h-[52px] w-full items-center justify-center gap-2.5 rounded-control border border-line bg-surface px-4 text-[15px] font-semibold shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    passkeysOn ? 'text-ink hover:bg-surface-hover' : 'cursor-not-allowed text-ink-3 shadow-none',
                    !optionsLoaded && 'invisible',
                  )}
                  data-testid="login-passkey"
                  data-enabled={passkeysOn ? 'true' : 'false'}
                >
                  {busy === 'passkey'
                    ? <Loader2 className="h-[18px] w-[18px] animate-spin" aria-hidden="true" />
                    : <PasskeyIcon className={cn('h-[18px] w-[18px]', passkeysOn && 'text-brand-ink')} aria-hidden="true" />}
                  {passkeyText}
                  {!passkeysOn && optionsLoaded ? (
                    <span className="rounded-full bg-[color:var(--status-gray-bg)] px-2 py-px text-[11px] font-semibold text-ink-3">Not set up yet</span>
                  ) : null}
                </button>
              ) : (
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={continueWithPasskey}
                    disabled={!passkeysOn || Boolean(busy) || loading}
                    title={passkeysOn || !optionsLoaded ? undefined : `Passkey sign-in ${NOT_SET_UP_HINT}`}
                    aria-hidden={optionsLoaded ? undefined : true}
                    className={cn(
                      'inline-flex min-h-[44px] items-center gap-2 rounded px-1 text-[13px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:min-h-0 lg:py-1',
                      passkeysOn ? 'text-brand-ink hover:underline' : 'cursor-not-allowed text-ink-3',
                      !optionsLoaded && 'invisible',
                    )}
                    data-testid="login-passkey"
                    data-enabled={passkeysOn ? 'true' : 'false'}
                  >
                    {busy === 'passkey' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <PasskeyIcon className="h-4 w-4" aria-hidden="true" />}
                    {passkeyText}
                    {!passkeysOn && optionsLoaded ? <span className="font-normal">· not set up yet</span> : null}
                  </button>
                </div>
              )
            ) : null}
          </form>
        </div>
      </main>
    </div>
  );
}
